import type { RfqSubmission } from '../types';
import { submissionQuoteTotal } from './rfqQuoteTotal';

function createdAtMs(sub: RfqSubmission): number {
  return sub.createdAt instanceof Date ? sub.createdAt.getTime() : 0;
}

function isUsableQuote(sub: RfqSubmission): boolean {
  return !sub.notBidding && !!(sub.storagePath || sub.cncQuote);
}

/**
 * Which CNC upload is the standing award. CNC approvals are per supplier org,
 * not per submission, so this infers the file that was current when staff
 * awarded — otherwise a later revision hides the approved quote.
 */
export function resolveCncAwardedQuote(args: {
  quotes: RfqSubmission[];
  approvedAt?: Date | null;
  billCreatedAt?: Date | null;
  billAmount?: number | null;
}): RfqSubmission | null {
  const quotes = args.quotes.filter(isUsableQuote);
  if (!quotes.length) return null;

  const awardTime =
    (args.approvedAt instanceof Date && !Number.isNaN(args.approvedAt.getTime())
      ? args.approvedAt
      : null)
    ?? (args.billCreatedAt instanceof Date && !Number.isNaN(args.billCreatedAt.getTime())
      ? args.billCreatedAt
      : null);

  if (awardTime) {
    const cutoff = awardTime.getTime() + 2000;
    const eligible = quotes.filter(s => createdAtMs(s) <= cutoff);
    if (eligible.length) {
      return eligible.slice().sort((a, b) => createdAtMs(b) - createdAtMs(a))[0] ?? null;
    }
  }

  const amount = args.billAmount;
  if (typeof amount === 'number' && amount > 0) {
    const rounded = Math.round(amount);
    const match = quotes.find(s => {
      const total = submissionQuoteTotal(s);
      return total != null && Math.round(total) === rounded;
    });
    if (match) return match;
  }

  return quotes.slice().sort((a, b) => createdAtMs(b) - createdAtMs(a))[0] ?? null;
}
